import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { useAction } from "../../../components/ui";
import { BetragErkannt, betragUnlesbar } from "../../../components/Erkannt";
import { useFormularDaten, useHeute } from "../kooperationen/hooks";
import { katalogAuswahl } from "../kooperationen/model";
import { speichereAnfrage } from "./daten";
import { useAnfrage, useAnfragen, useKontakte } from "./hooks";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { NEW_BRAND, SOURCE_LABEL, exklusivHinweise, formularAusAnfrage, leeresFormular, nameOf, pruefeFormular, type AnfrageFormular as Formular } from "./model";
import "../kooperationen/kooperationen.css";
import { t } from "../../../lib/sprache";

/** Neue Anfrage (?creator=, ?marke= vorbelegt) */
export function AnfrageNeu() {
  return <AnfrageFormular />;
}
/** Anfrage bearbeiten */
export function AnfrageBearbeiten() {
  const { inquiryId } = useParams();
  return <AnfrageFormular id={inquiryId} />;
}

/**
 * Formular der Anfrage (0072): Marke (auch neu), Ansprechperson, Titel, Eingang und Quelle, verantwortliche Person, Zeitraum,
 * Budget der Marke, Plattformen, Wünsche (Nutzungsrechte, Exklusivität, Eile), Briefing und die vorgeschlagenen Creator
 * (Casting). Die Exklusivitätsprüfung der Kooperationen warnt schon hier, wenn ein Creator im Zeitraum gebunden ist.
 */
function AnfrageFormular({ id }: { id?: string }) {
  const ws = useStaffWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const today = useHeute();
  const fd = useFormularDaten();
  const liste = useAnfragen();
  const detail = useAnfrage(id);
  const { busy, error, run } = useAction();
  // Tarifstaffel (0099): neu anlegen erst mit „Anfragen und Angebote“; bestehende Anfragen bleiben bearbeitbar
  const neuGesperrt = useModul("module_pipeline").gesperrt && !id;
  const [f, setF] = useState<Formular | null>(null);
  const [fehler, setFehler] = useState<string[]>([]);
  const kontakte = useKontakte(f?.brandId ?? "");

  useEffect(() => {
    if (f || !fd.data) return;
    if (id) {
      if (detail.data) setF(formularAusAnfrage(detail.data.inquiry, detail.data.candidates));
      return;
    }
    setF(leeresFormular({ today, currency: fd.data.settings?.currency ?? ws.org.currency, userId: api.session()?.userId ?? null,
      brandId: params.get("marke") ?? undefined, talentId: params.get("creator") ?? undefined }));
  }, [f, fd.data, detail.data, id, today, params, ws.org.currency]);

  // CO-019: hat die Marke genau einen Kontakt, ist er bei einer neuen Anfrage gleich die Ansprechperson (einmal je Marke, „Keine“ bleibt wählbar)
  const [kontaktVorbelegt, setKontaktVorbelegt] = useState("");
  useEffect(() => {
    if (id || !f?.brandId || f.contactId || kontaktVorbelegt === f.brandId || kontakte.data?.length !== 1) return;
    if (kontakte.data[0]!.brand_id !== f.brandId) return;
    setKontaktVorbelegt(f.brandId);
    setF((x) => (x ? { ...x, contactId: kontakte.data![0]!.id } : x));
  }, [id, f?.brandId, f?.contactId, kontaktVorbelegt, kontakte.data]);

  if (fd.error) return <ErrorState error={fd.error} retry={() => void fd.refetch()} />;
  if (detail.error) return <ErrorState error={detail.error} retry={() => void detail.refetch()} />;
  if (id && detail.data === null) return <NoAccess text={t("Diese Anfrage gibt es nicht oder sie gehört zu einer anderen Agentur.", "This inquiry does not exist or belongs to another agency.")} />;
  if (!fd.data || !f) return <Loading />;
  const data = fd.data;
  const update = (patch: Partial<Formular>) => setF((x) => (x ? { ...x, ...patch } : x));
  const plattformen = katalogAuswahl(data.platforms, ["instagram", "tiktok", "youtube"], f.platforms);
  const currencies = data.settings?.currencies?.length ? data.settings.currencies : [data.settings?.currency ?? ws.org.currency];
  const creatorAuswahl = data.talents.filter((t) => t.status !== "archived" || f.talents.includes(t.id));
  const kampagnen = data.campaigns.filter((c) => c.brand_id === f.brandId && (c.status === "active" || c.id === f.campaignId));
  const hinweise = exklusivHinweise({ ...f, today }, { exklusiv: data.exklusiv, exklusivMonate: data.exklusivMonate, brands: data.brands, talents: data.talents });
  const offerExists = !!detail.data?.offers.some((o) => o.status !== "draft");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (neuGesperrt) return;
    const r = pruefeFormular(f!, { betraege: ws.can("view_amounts") });
    setFehler(r.fehler);
    if (!r.values) return;
    const values = id ? r.values : { ...r.values, org_id: ws.org.org_id };
    const res = await run("speichern", () => speichereAnfrage(api, id ?? null, values, f!.talents, r.newBrand, detail.data?.inquiry.updated_at ?? null),
      id ? t("Anfrage gespeichert.", "Inquiry saved.") : t("Anfrage angelegt.", "Inquiry created."));
    if (res) {
      if (res.kept.length) setFehler([t(`${res.kept.length === 1 ? t("1 Creator bleibt", "1 creator remains") : t(`${res.kept.length} Creator bleiben`, `${res.kept.length} creators remain`)} in der Anfrage, weil es schon eine Entscheidung oder ein festgeschriebenes Angebot gibt.`, `${res.kept.length === 1 ? t("1 Creator bleibt", "1 creator remains") : t(`${res.kept.length} Creator bleiben`, `${res.kept.length} creators remain`)} in the inquiry because there is already a decision or a finalised offer.`)]);
      navigate(`/agentur/anfragen/${res.inquiry_id}`);
    }
  }

  return (
    <main id="main" className="content tk-koop tk-anfragen tk-formular">
      <div className="pagehead">
        <div>
          <div className="eyebrow"><Link to="/agentur/anfragen">{t("Anfragen", "Inquiries")}</Link></div>
          <h1>{id ? t("Anfrage bearbeiten", "Edit inquiry") : t("Neue Anfrage", "New inquiry")}</h1>
          <p>{t("Wer fragt was an? Creator lassen sich als Casting vorschlagen; je Creator gibt es später eine eigene Zu- oder Absage.", "Who is asking for what? Creators can be proposed as a casting; each creator later gets their own acceptance or decline.")}</p>
        </div>
      </div>
      {neuGesperrt && <TarifHinweis modul="module_pipeline" id="anfrage-neu-tarif" was={t("Neue Anfragen und Angebote", "New inquiries and offers")} />}
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        <section className="panel stack">
          <h2>{t("Marke und Anfrage", "Brand and inquiry")}</h2>
          <div className="grid">
            <label>
              {t("Marke", "Brand")}<select id="marke" value={f.brandId} disabled={offerExists} onChange={(e) => update({ brandId: e.target.value, contactId: "", campaignId: "" })}>
                <option value="">{t("Marke wählen …", "Choose brand …")}</option>
                {data.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                {!id && <option value={NEW_BRAND}>{t("Neue Marke anlegen …", "Create new brand …")}</option>}
              </select>
            </label>
            {f.brandId === NEW_BRAND && <label>{t("Name der neuen Marke", "Name of the new brand")}<input type="text" maxLength={120} value={f.newBrand} onChange={(e) => update({ newBrand: e.target.value })} /></label>}
            {f.brandId && f.brandId !== NEW_BRAND && (
              <label>
                {t("Ansprechperson (für das Angebot)", "Contact person (for the offer)")}<select value={f.contactId} onChange={(e) => update({ contactId: e.target.value })}>
                  <option value="">{t("Keine", "None")}</option>
                  {(kontakte.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</option>)}
                </select>
              </label>
            )}
            <label>{t("Titel", "Title")}<input id="titel" type="text" maxLength={120} required value={f.title} onChange={(e) => update({ title: e.target.value })} placeholder={t("z. B. Herbstkampagne Hautpflege", "e.g. autumn skincare campaign")} /></label>
            <label>
              {t("Quelle", "Source")}<select value={f.source} onChange={(e) => update({ source: e.target.value })}>
                <option value="">{t("Keine Angabe", "Not specified")}</option>
                {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label>{t("Eingang am", "Received on")}<input type="date" value={f.receivedOn} onChange={(e) => update({ receivedOn: e.target.value })} /></label>
            {/* Studio (Creator-Konto): kein Team, also keine Zuständigkeit */}
            {!ws.isCreator && <label>
              {t("Verantwortlich", "Responsible")}<select value={f.managerId} onChange={(e) => update({ managerId: e.target.value })}>
                <option value="">{t("Niemand (ganzes Team)", "Nobody (whole team)")}</option>
                {(liste.data?.members ?? []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </label>}
            {f.brandId && f.brandId !== NEW_BRAND && (
              <label>
                {t("Kampagne (optional)", "Campaign (optional)")}<select value={f.campaignId} onChange={(e) => update({ campaignId: e.target.value })}>
                  <option value="">{t("Keine; bei der Zusage anlegen oder weglassen", "None; create on acceptance or leave out")}</option>
                  {kampagnen.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </label>
            )}
          </div>
        </section>

        <section className="panel stack">
          <h2>{t("Zeitraum, Budget und Wünsche der Marke", "Period, budget and brand wishes")}</h2>
          <div className="grid">
            <label>{t("Beginn", "Start")}<input type="date" value={f.startOn} onChange={(e) => update({ startOn: e.target.value })} /></label>
            <label>{t("Ende", "End")}<input type="date" value={f.endOn} onChange={(e) => update({ endOn: e.target.value })} /></label>
            {ws.can("view_amounts") && <label>{t("Budget der Marke (optional)", "Brand budget (optional)")}<input inputMode="decimal" value={f.budget} aria-invalid={betragUnlesbar(f.budget) || undefined} onChange={(e) => update({ budget: e.target.value })} placeholder="z. B. 5.000" /><BetragErkannt text={f.budget} currency={f.currency} /></label>}
            {currencies.length > 1 ? (
              <label>
                {t("Währung", "Currency")}<select value={f.currency} disabled={!!detail.data?.offers.length} onChange={(e) => update({ currency: e.target.value })}>
                  {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            ) : <p className="source-note">{t("Währung: ", "Currency: ")}{f.currency}</p>}
            <label>{t("Nutzungsrechte (Paid Social) in Monaten", "Usage rights (paid social) in months")}<input inputMode="numeric" value={f.usageMonths} onChange={(e) => update({ usageMonths: e.target.value })} placeholder={t("leer: keine", "empty: none")} /></label>
          </div>
          <fieldset>
            <legend>{t("Plattformen", "Platforms")}</legend>
            <div className="tk-koop-weitere-liste">
              {plattformen.map((p) => (
                <label key={p.key} className="row tk-koop-wahl">
                  <input type="checkbox" checked={f.platforms.includes(p.key)}
                    onChange={(e) => update({ platforms: e.target.checked ? [...f.platforms, p.key] : f.platforms.filter((x) => x !== p.key) })} />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid">
            <label className="row tk-koop-wahl"><input type="checkbox" checked={f.rush} onChange={(e) => update({ rush: e.target.checked })} /><span>{t("Eilig (kurze Vorlaufzeit, Eilzuschlag)", "Urgent (short lead time, rush surcharge)")}</span></label>
            <label className="row tk-koop-wahl"><input type="checkbox" checked={f.exclusive} onChange={(e) => update({ exclusive: e.target.checked })} /><span>{t("Die Marke verlangt Exklusivität", "The brand requires exclusivity")}</span></label>
            {f.exclusive && <label>{t("Exklusiv bis", "Exclusive until")}<input type="date" value={f.exclusiveUntil} onChange={(e) => update({ exclusiveUntil: e.target.value })} /></label>}
            {f.exclusive && <label>{t("Branche oder Wettbewerber", "Industry or competitors")}<input type="text" maxLength={500} value={f.exclusiveScope} onChange={(e) => update({ exclusiveScope: e.target.value })} placeholder={t("z. B. Hautpflege, keine Drogeriemarken", "e.g. skincare, no drugstore brands")} /></label>}
          </div>
          <label>Briefing<textarea rows={4} maxLength={8000} value={f.briefing} onChange={(e) => update({ briefing: e.target.value })} /></label>
          <label>{t("Interne Notiz", "Internal note")}<textarea rows={2} maxLength={2000} value={f.note} onChange={(e) => update({ note: e.target.value })} /></label>
        </section>

        <section className="panel stack">
          <h2>{t("Creator vorschlagen", "Propose creators")}</h2>
          <p className="source-note">{t("Mehrere Creator ergeben ein Casting: ein Angebot mit Positionen je Creator, jede Zusage wird eine eigene Kooperation.", "Several creators make a casting: one offer with items per creator, each acceptance becomes its own deal.")}</p>
          <div className="tk-koop-weitere-liste">
            {creatorAuswahl.map((t) => (
              <label key={t.id} className="row tk-koop-wahl">
                <input type="checkbox" checked={f.talents.includes(t.id)}
                  onChange={(e) => update({ talents: e.target.checked ? [...f.talents, t.id] : f.talents.filter((x) => x !== t.id) })} />
                <span>{nameOf(t)}</span>
              </label>
            ))}
          </div>
          {hinweise.length > 0 && (
            <div className="notice tk-koop-warnung" role="status">
              <strong>{t("Exklusivität prüfen", "Check exclusivity")}</strong>
              <ul>{hinweise.map((h) => <li key={h}>{h}</li>)}</ul>
            </div>
          )}
        </section>

        {(fehler.length > 0 || error) && (
          <div className="notice tk-koop-warnung" role="alert">
            <ul>{fehler.map((x) => <li key={x}>{x}</li>)}{error && <li>{error}</li>}</ul>
          </div>
        )}
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy || neuGesperrt} aria-describedby={neuGesperrt ? "anfrage-neu-tarif" : undefined}>{busy ? t("Wird gespeichert …", "Saving …") : id ? t("Änderungen speichern", "Save changes") : t("Anfrage anlegen", "Create inquiry")}</button>
          <Link className="btn" to={id ? `/agentur/anfragen/${id}` : "/agentur/anfragen"}>{t("Abbrechen", "Cancel")}</Link>
        </div>
      </form>
    </main>
  );
}
